import type { getAllPaymentsOutput } from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { STATUS_COLOR_TO_CHIP_VARIANT } from "@/pages/dashboard/components";
import { useGetAllPayments } from "@/services";
import { dateUtils } from "@/utils/time";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import { getPaymentStateTranslation } from "@/utils/translations";
import {
	Chip,
	DataTable,
	Typography,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

type PaymentRow = getAllPaymentsOutput["results"][number];

const field = (row: PaymentRow, key: string): string | null =>
	key in row ? ((row as Record<string, unknown>)[key] as string | null) : null;

/** Who paid whom: payer -> payee, for one payment type over every user. */
export function PaymentsTable({
	paymentType,
}: {
	paymentType: "system" | "market";
}) {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const server = useServerTableState();

	const { data, isLoading, isFetching, error, refetch } = useGetAllPayments({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			payment_type: paymentType,
		},
	});

	const columns = useMemo<ColumnDef<PaymentRow>[]>(
		() => [
			{
				id: "payer",
				header: t("admin.payments.payer"),
				enableSorting: false,
				cell: ({ row }) =>
					(paymentType === "system"
						? field(row.original, "seller_name")
						: field(row.original, "buyer_name")) ?? "-",
			},
			{
				id: "payee",
				header: t("admin.payments.payee"),
				enableSorting: false,
				cell: ({ row }) =>
					paymentType === "system" ? (
						<Typography variant="body-sm" className="text-secondary-icon">
							{t("admin.payments.system")}
						</Typography>
					) : (
						field(row.original, "seller_name") ?? "-"
					),
			},
			{
				id: "amount",
				header: t("payment.paid_amount"),
				enableSorting: false,
				cell: ({ row }) => (
					<span className="tabular-nums">
						{formatNumber(row.original.amount, "price")}
					</span>
				),
			},
			{
				id: "state",
				header: t("payment.status"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = getPaymentStateTranslation(
						row.original.state,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "type",
				header: t("payment.payment_method"),
				enableSorting: false,
				cell: ({ row }) => t(`payment.type.${row.original.type}` as never),
			},
			{
				id: "payment_note",
				header: t("admin.payments.note"),
				enableSorting: false,
				cell: ({ row }) => (
					<span className="block max-w-72 truncate">
						{field(row.original, "payment_note") ?? "-"}
					</span>
				),
			},
			{
				id: "created_at",
				header: t("payment.created_at"),
				enableSorting: false,
				cell: ({ row }) => {
					const created = new Date(row.original.created_at);
					return (
						<span
							className="whitespace-nowrap"
							title={dateUtils.format(created, "d MMM yyyy, HH:mm")}
						>
							{getTimeSinceText(created, t, lang)}
						</span>
					);
				},
			},
		],
		[t, lang, paymentType],
	);

	return (
		<DataTable
			columns={columns}
			data={data?.results ?? []}
			totalItems={data?.count ?? 0}
			server={server}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error ? getErrorInterface(error) : null}
			onRetry={refetch}
			getRowId={(row) => row.id}
		/>
	);
}
